import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { HubEditorial } from "@/components/site/HubEditorial";
import { HUB_EDITORIAL } from "@/data/hub-editorial";
import { socialImageMeta } from "@/lib/social";
import { ShareBar } from "@/components/site/ShareBar";
import { ArticleDisclaimer } from "@/components/site/ArticleDisclaimer";
import { makeSeoDescription } from "@/lib/seo-description";

const CANONICAL = "https://lifewestway.com/cross-border-wealth";
const TITLE = "Cross-Border Wealth & Offshore Banking 2026 | LifeWestWay";

export const Route = createFileRoute("/cross-border-wealth")({
  head: () => ({
    meta: [
      { title: TITLE },
      {
        name: "description",
        content: makeSeoDescription(
          "Legal offshore banking and cross-border wealth guides for USA, UK, Australia, Italy, Portugal and Canada — FBAR, CRS 2.0, Quadro RW, T1135 and crypto rules."
        ),
      },
      { property: "og:title", content: TITLE },
      { property: "og:site_name", content: "LifeWestWay" },
      {
        property: "og:description",
        content: makeSeoDescription(
          "Six countries, six 2026 disclosure playbooks — how to hold money abroad legally, what each tax authority already sees, and how to report it correctly."
        ),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      ...socialImageMeta(),
      { name: "twitter:title", content: TITLE },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: CrossBorderWealthHub,
});

type Guide = {
  country: string;
  flag: string;
  accent: string;
  title: string;
  blurb: string;
  highlight: string;
  to: string;
  params: Record<string, string>;
};

const GUIDES: Guide[] = [
  {
    country: "United States",
    flag: "🇺🇸",
    accent: "from-sky-500/25 to-indigo-400/10",
    title: "What's Legal for US Persons in 2026",
    blurb:
      "Citizenship-based taxation, the FBAR USD 10,000 aggregate rule, Form 8938 thresholds, CRS 2.0 crypto reporting and the legal ways to move money across borders.",
    highlight: "FBAR + FATCA + CARF explained",
    to: "/usa/$slug",
    params: { slug: "cross-border-wealth-offshore-banking-usa-2026" },
  },
  {
    country: "United Kingdom",
    flag: "🇬🇧",
    accent: "from-indigo-500/25 to-sky-400/10",
    title: "What's Legal for UK Residents in 2026",
    blurb:
      "What HMRC already receives through CRS, the new Foreign Income and Gains regime, CARF crypto reporting and the Worldwide Disclosure Facility.",
    highlight: "CRS data, FIG regime & WDF",
    to: "/uk/$slug",
    params: { slug: "cross-border-wealth-offshore-banking-uk-2026" },
  },
  {
    country: "Australia",
    flag: "🇦🇺",
    accent: "from-amber-500/25 to-emerald-400/10",
    title: "What's Legal for Australians in 2026",
    blurb:
      "Worldwide income rules, CRS data sharing, CARF crypto reporting, AUSTRAC transfer visibility and ATO voluntary disclosure.",
    highlight: "Worldwide income + CRS/CARF",
    to: "/australia/$slug",
    params: { slug: "cross-border-wealth-offshore-banking-australia-2026" },
  },
  {
    country: "Canada",
    flag: "🇨🇦",
    accent: "from-red-500/25 to-rose-400/10",
    title: "What's Legal for Canadian Residents in 2026",
    blurb:
      "Worldwide income under the CRA, the CAD $100,000 cost-based T1135 test, the first-year newcomer exemption, CRS data matching and CARF crypto reporting.",
    highlight: "T1135 · CRS · CARF crypto",
    to: "/canada/$slug",
    params: { slug: "cross-border-wealth-offshore-banking-canada-2026" },
  },
  {
    country: "Italy",
    flag: "🇮🇹",
    accent: "from-emerald-500/25 to-rose-500/10",
    title: "What's Legal for Italian Residents in 2026",
    blurb:
      "Quadro RW asset reporting, IVIE and IVAFE wealth taxes, CRS data sharing, CARF crypto rules and Italy's flat tax regimes.",
    highlight: "Quadro RW + IVIE/IVAFE",
    to: "/italy/$slug",
    params: { slug: "cross-border-wealth-offshore-banking-italy-2026" },
  },
  {
    country: "Portugal",
    flag: "🇵🇹",
    accent: "from-emerald-500/25 to-amber-400/10",
    title: "What's Legal for Portugal Residents in 2026",
    blurb:
      "Worldwide income under IRS, CRS data sharing, tightened crypto tax rules, the end of old NHR and the much narrower IFICI regime.",
    highlight: "NHR closed — IFICI is narrow",
    to: "/portugal/$slug",
    params: { slug: "cross-border-wealth-offshore-banking-portugal-2026" },
  },
];

function CrossBorderWealthHub() {
  return (
    <main id="main-content" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Breadcrumbs
        items={[{ label: "Home", href: "/" }, { label: "Cross-Border Wealth & Offshore Banking" }]}
      />

      <section className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-card/80 via-card/40 to-background/60 p-8 sm:p-12">
        <span
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-accent/20 blur-3xl"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-accent/10 blur-3xl"
        />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
            <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
            6 Countries · Compliance-First · 2026
          </span>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Cross-Border Wealth &amp;{" "}
            <span className="text-accent">Offshore Banking</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base sm:text-lg text-foreground/75">
            Holding money abroad is legal. Hiding it is not. These 2026 guides explain the
            reporting rules that actually apply in the USA, UK, Australia, Canada, Italy and
            Portugal — FBAR and FATCA, CRS 2.0, Quadro RW, Anexo J and Form T1135 — plus the
            new crypto reporting framework that started this January.
          </p>
          <p className="mt-4 max-w-3xl rounded-2xl border border-accent/30 bg-accent/5 p-4 text-sm text-foreground/80">
            <strong className="text-accent">Editorial note:</strong> this series covers legal
            compliance and disclosure only. We do not publish tax-avoidance techniques,
            secrecy strategies or &ldquo;loopholes&rdquo;, and we recommend only regulated
            money-transfer providers.
          </p>
        </div>
      </section>

      <section
        aria-label="Cross-border wealth guides by country"
        className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3"
      >
        {GUIDES.map((g) => (
          <Link title={`Read the cross-border wealth guide: ${g.title}`}
            key={g.title}
            to={g.to}
            params={g.params}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/50 p-6 transition-all hover:border-accent/60 hover:bg-card/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span
              aria-hidden
              className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${g.accent} opacity-0 transition-opacity group-hover:opacity-100`}
            />
            <div className="relative flex items-center justify-between">
              <span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/40 px-3 py-1 text-xs font-semibold text-foreground/80">
                <span aria-hidden className="text-base leading-none">
                  {g.flag}
                </span>
                {g.country}
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent/90">
                2026
              </span>
            </div>

            <h2 className="relative mt-4 font-display text-xl font-bold tracking-tight text-foreground group-hover:text-accent">
              {g.title}
            </h2>
            <p className="relative mt-3 text-sm text-foreground/70">{g.blurb}</p>

            <div className="relative mt-5 flex items-center justify-between border-t border-border/50 pt-4">
              <span className="text-xs font-medium text-foreground/60">
                <span className="text-accent">◆</span> {g.highlight}
              </span>
              <span className="text-sm font-semibold text-accent">
                Read guide <span aria-hidden>→</span>
              </span>
            </div>
          </Link>
        ))}
      </section>

      <HubEditorial data={HUB_EDITORIAL["cross-border-wealth"]} />

      <ArticleDisclaimer />

      <ShareBar />
    </main>
  );
}
